// Constants
const COLORS = {
  primary: "rgb(31, 58, 112)",
  text: "#111827",
  textLight: "#6b7280",
  border: "#e5e7eb",
  background: "#f9fafb",
  backgroundLight: "#eef2f7",
  white: "#ffffff",
  success: "#22c55e",
  blue: "#3b82f6",
  gray: "#ccc",
  darkGray: "#666",
};

const BREAKPOINTS = {
  mobile: "80px",
  desktop: "0px",
};

export const styles = {
  // ===== Layout =====
  container: (isMobile) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    height: "100vh",
    backgroundColor: COLORS.backgroundLight,
    paddingBottom: isMobile ? BREAKPOINTS.mobile : BREAKPOINTS.desktop,
  }),

  header: {
    padding: "14px 18px",
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  h2: (isMobile) => ({
    margin: 0,
    fontSize: isMobile ? "13px" : "16px",
    fontWeight: "600",
    color: COLORS.text,
    lineHeight: 1.2,
    whiteSpace: isMobile ? "normal" : "nowrap",
    overflow: isMobile ? "hidden" : "visible",
    textOverflow: isMobile ? "ellipsis" : "clip",
    wordBreak: isMobile ? "break-word" : "normal",
    maxWidth: isMobile ? "100%" : "none",
    minWidth: 0,
  }),

  messagesContainer: {
    flex: 1,
    padding: "20px 16px",
    overflowY: "auto",
    backgroundColor: COLORS.backgroundLight
  },

  inputContainer: (isMobile) => ({
    position: "relative",
    padding: "12px",
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: isMobile ? BREAKPOINTS.mobile : "12px",
  }),

  // ===== Messages =====
  message: (sender) => ({
    display: "flex",
    justifyContent: sender === "EU" ? "flex-end" : "flex-start",
    marginBottom: "12px",
  }),

  messageBubble: (sender) => ({
    padding: "10px 18px",
    borderRadius: "16px",
    backgroundColor: sender === "EU" ? COLORS.primary : COLORS.white,
    color: sender === "EU" ? COLORS.white : COLORS.text,
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    maxWidth: "72%",
    fontSize: "14px",
    lineHeight: "1.4",
  }),

  messageUser: (sender) => ({
    display: sender === "EU" ? "none" : "block",
    fontWeight: "600",
    fontSize: "12px",
    color: COLORS.textLight,
    marginBottom: 2,
  }),

  messageText: {
    margin: 0,
  },

  messageTime: {
    fontSize: "10px",
    color: "rgba(255,255,255,0.7)",
    alignSelf: "flex-end",
    marginTop: 4,
  },

  messageMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    marginTop: 4,
  },

  messageStatusIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.85)",
    lineHeight: 1,
  },

  systemMessageWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 14,
    width: "100%",
  },

  systemMessageBubble: {
    maxWidth: "84%",
    padding: "10px 14px",
    borderRadius: 18,
    backgroundColor: "rgba(255,255,255,0.9)",
    color: COLORS.textLight,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    textAlign: "center",
    lineHeight: 1.4,
  },

  systemMessageText: {
    margin: 0,
    fontSize: 13,
    color: COLORS.text,
  },

  systemMessageTime: {
    display: "block",
    marginTop: 4,
    fontSize: 11,
    color: COLORS.textLight,
  },

  // ===== Input =====
  input: {
    flex: 1,
    padding: "12px 16px",
    borderRadius: "999px",
    border: `1px solid ${COLORS.border}`,
    outline: "none",
    fontSize: "14px",
    backgroundColor: COLORS.background,
  },

  mentionMenu: {
    position: "absolute",
    bottom: "70px",
    left: "12px",
    right: "66px",
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    zIndex: 1000,
    maxHeight: 220,
    overflowY: "auto",
  },

  mentionItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    cursor: "pointer",
    borderBottom: `1px solid #f3f4f6`,
  },

  mentionEmpty: {
    padding: "12px 14px",
    color: COLORS.textLight,
    fontSize: 13,
  },

  mentionAvatarWrapper: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
    backgroundColor: COLORS.background,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  mentionAvatar: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  mentionDescription: {
    fontSize: 12,
    color: COLORS.textLight,
    marginTop: 2,
  },

  sendButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #3159a8, #1f3a70)",
    color: COLORS.white,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 10px rgba(31, 58, 112, 0.22)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },

  // ===== Upload Progress =====
  progressBarContainer: {
    marginTop: 8,
    width: "100%",
    height: 4,
    background: COLORS.border,
    borderRadius: 4,
    overflow: "hidden",
  },

  progressBar: (progress) => ({
    width: `${progress || 0}%`,
    height: "100%",
    background: COLORS.blue,
    transition: "width 0.2s ease",
  }),

  // ===== Notifications =====
  toast: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    padding: "12px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 9999,
    boxShadow: "0 4px 12px rgba(31, 58, 112, 0.3)",
    maxWidth: "85%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    animation: "slideUp 0.3s ease-in-out",
  },

  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.6)",
    zIndex: 999,
  },

  spinner: {
    width: 45,
    height: 45,
    border: `4px solid ${COLORS.border}`,
    borderTop: `4px solid ${COLORS.primary}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  // ===== Day Separator =====
  daySeparatorContainer: {
    display: "flex",
    justifyContent: "center",
    margin: "12px 0",
  },

  daySeparatorLabel: {
    padding: "4px 12px",
    borderRadius: 12,
    backgroundColor: COLORS.white,
    color: COLORS.text,
    fontSize: 12,
    fontWeight: 500,
    textTransform: "capitalize",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.06)",
  },

  systemMessageWrapper: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginBottom: 12,
  },

  systemMessageBubble: {
    maxWidth: "75%",
    backgroundColor: "rgba(59, 130, 246, 0.12)",
    color: COLORS.text,
    padding: "10px 14px",
    borderRadius: 16,
    fontSize: 13,
    textAlign: "center",
    boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
    display: "inline-flex",
    flexDirection: "column",
    gap: 4,
  },

  systemMessageText: {
    margin: 0,
    color: COLORS.text,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
  },

  systemMessageTime: {
    fontSize: 11,
    color: COLORS.textLight,
    alignSelf: "center",
  },

  // ===== Typing Indicator =====
  typingIndicator: (isMe) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "10px 18px",
    borderRadius: "16px",
    backgroundColor: isMe ? COLORS.primary : COLORS.white,
    maxWidth: "65%",
    gap: 4,
  }),

  typingDot: {
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: COLORS.text,
    animation: "typing 1.2s infinite",
  },

  // ===== Header Elements =====
  headerMenuButton: {
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    color: COLORS.primary,
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  },

  backButton: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    display: "flex",
    padding: 0,
    flexShrink: 0,
  },

  conversationLogo: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    objectFit: "cover",
  },

  conversationPlaceholder: {
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e0e0e0",
    borderRadius: "50%",
  },

  headerLeft: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: isMobile ? "1 1 auto" : "none",
    maxWidth: isMobile ? "calc(100% - 50px)" : "none",
  }),

  headerTop: (isMobile) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: isMobile ? 10 : 12,
    minWidth: 0,
    flex: isMobile ? 1 : "none",
    minHeight: 40,
  }),

  menuDropdown: {
    position: "absolute",
    top: 30,
    right: 0,
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    boxShadow: "0 10px 28px rgba(31, 58, 112, 0.14)",
    padding: 6,
    zIndex: 100,
  },

  menuItemOption: {
    padding: "9px 12px",
    cursor: "pointer",
    fontSize: 14,
    color: COLORS.text,
    whiteSpace: "nowrap",
    borderRadius: 7,
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "background-color 0.18s ease",
  },

  menuItemOptionHover: {
    background: "#f5f5f5",
  },

  menuContainer: {
    position: "relative",
    flex: "0 0 auto",
  },


  participantsContainer: (isMobile) => ({
    display: "flex",
    flexWrap: isMobile ? "nowrap" : "wrap",
    gap: isMobile ? 8 : 10,
    marginTop: 2,
    padding: isMobile ? "4px 2px" : "4px 0",
    overflowX: isMobile ? "auto" : "visible",
    alignItems: "center",
    minHeight: 36,
    width: "100%",
    boxSizing: "border-box",
  }),

  participantItem: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    flex: "0 0 auto",
    minWidth: 0,
    maxWidth: isMobile ? 120 : "none",
  }),

  participantAvatarWrapper: {
    position: "relative",
    display: "inline-block",
    overflow: "visible",
  },

  participantAvatar: (isMe) => ({
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgb(204, 204, 204)",
    borderRadius: "50%",
    overflow: "hidden",
    border: "1px solid #D8E0EA",
    boxShadow: isMe ? `0 0 0 2px rgba(34,197,94,0.18)` : "none",
    flexShrink: 0,
  }),

  participantName: {
    fontSize: 11,
    color: COLORS.darkGray,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  participantExtraBadge: (isMobile) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: isMobile ? 28 : 34,
    height: 26,
    padding: "0 9px",
    borderRadius: 16,
    border: "1px solid #E5EAF1",
    backgroundColor: "#F5F7FA",
    color: "#53657D",
    fontSize: isMobile ? 10 : 12,
    fontWeight: 600,
    flex: "0 0 auto",
    whiteSpace: "nowrap",
  }),

  crownIcon: {
    position: "absolute",
    bottom: -6,
    right: -6,
  },

  // ===== Message Styling =====
  messageContainer: {
    display: "flex",
    marginBottom: 10,
    alignItems: "flex-start",
  },

  messageContainerReverse: {
    display: "flex",
    marginBottom: 10,
    flexDirection: "row-reverse",
    alignItems: "flex-start",
  },

  messageAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },

  messageAvatarMargin: (isMe) => ({
    marginRight: isMe ? 0 : 8,
    marginLeft: isMe ? 8 : 0,
  }),

  messageAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: COLORS.gray,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
  },

  // ===== File Attachment =====
  fileAttachment: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    background: "#f3f4f6",
    borderRadius: 8,
    textDecoration: "none",
    color: COLORS.text,
    maxWidth: 220,
  },

  fileMetaContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    opacity: 0.9,
    whiteSpace: "nowrap",
  },

  fileName: {
    fontWeight: 500,
    fontSize: 13,
  },

  // ===== Image/Video =====
  imageMessage: {
    maxWidth: 200,
    borderRadius: 8,
  },

  videoMessage: {
    maxWidth: 250,
    borderRadius: 8,
    display: "block",
  },

 

  // ===== Buttons =====
  attachButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 6,
  },

  aiCallButton: {
    width: 42,
    height: 42,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    background: "linear-gradient(145deg, #ffffff, #eef3ff)",
    color: "#3159a8",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 2px 7px rgba(31, 58, 112, 0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
  },

  hiddenInput: {
    display: "none",
  },

  // Indicação visual da zona onde os ficheiros podem ser largados.
  fileDropOverlay: {
    // Aplica a animação fileDropIn, definida em ChatWindow.jsx.
    animation: "fileDropIn 0.2s ease-out",
    position: "absolute",
    inset: 12,
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #1f3a70",
    borderRadius: 16,
    background: "rgba(255, 255, 255, 0.9)",
    color: "#1f3a70",
    fontSize: 18,
    fontWeight: 600,
    pointerEvents: "none",
  },

  // ===== Empty State =====
  emptyState: {
    padding: 16,
  },

  scrollBottomButton: {
    pointerEvents: "auto",
    background: COLORS.primary,
    color: COLORS.white,
    border: "none",
    borderRadius: "50%",
    width: 46,
    height: 46,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "fadeInUp 0.25s ease forwards",
},

  scrollButtonWrapper: (isMobile) => ({
    position: isMobile ? "fixed" : "absolute",
    bottom: isMobile ? 160 : 80,
    left: isMobile ? "55%" : "50%",
    zIndex: 999,
    pointerEvents: "none",
  }),


  loadMoreContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "10px 0",
  },

  spinnerSmall: {
    width: 22,
    height: 22,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid rgb(31, 58, 112)",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  // ===== Apólices =====
  apolicesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 8,
    width: "100%",
    overflowX: "auto",
  },

  apolicesHeader: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.text,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  apolicesTable: {
    width: "100%",
    display: "table",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    fontSize: 13,
    backgroundColor: "#f9fafb",
    borderRadius: 6,
    overflow: "hidden",
    border: `1px solid ${COLORS.border}`,
    minWidth: 0,
  },

  apolicesTableHeader: {
    backgroundColor: "#e5e7eb",
  },

  apolicesTableCell: {
    padding: "16px 18px",
    textAlign: "left",
    borderRight: `1px solid ${COLORS.border}`,
    wordBreak: "break-word",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  apolicesTableCellLast: {
    padding: "16px 18px",
    textAlign: "left",
    wordBreak: "break-word",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  apolicesTableCellValue: {
    padding: "16px 18px",
    textAlign: "right",
    wordBreak: "break-word",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  apolicesTableRow: (isEven) => ({
    backgroundColor: isEven ? "#f9fafb" : COLORS.white,
    borderBottom: `1px solid ${COLORS.border}`,
  }),

  apolicesFooter: {
    fontSize: 12,
    fontWeight: 500,
    color: COLORS.text,
    paddingTop: 8,
    borderTop: `1px solid ${COLORS.border}`,
  },
};